import { TagTypes } from '../../../Api/Types/tagTypes';
import { cleanParams } from '../../Shared/Helpers/cleanParams';
import type { PagedList } from '../../Shared/Types/common';
import type {
  CashDocumentDto,
  CashBalanceResponse,
  CashDocumentType,
} from '../Types/cashDocumentTypes';
import type {
  CashReportCycleDetailsDto,
  CashReportCycleListItem,
  CreateCashReportCycleRequest,
  CreateCashReportCycleResponse,
  GetCashReportCyclesRequest,
} from '../Types/cashReportTypes';
import { cashDocumentsApi } from './cashDocumentsApi';

type PdfErrorResponse = Record<string, unknown> | string | null;
const CASH_REPORT_PDF_INVALID_RESPONSE_ERROR_CODE = 'CASH_REPORT_PDF_INVALID_RESPONSE';

const isPdfContentType = (contentType: string): boolean =>
  contentType.toLowerCase().includes('application/pdf');

const normalizePdfContentType = (contentType: string): string =>
  contentType.split(';')[0]?.trim().toLowerCase() || 'application/pdf';

const parseCashReportPdfResponse = async (response: Response): Promise<Blob | PdfErrorResponse> => {
  if (response.ok) {
    const blob = await response.blob();
    const contentTypeHeader = response.headers.get('content-type') ?? '';

    if (blob.type || !isPdfContentType(contentTypeHeader)) {
      return blob;
    }

    return new Blob([blob], { type: normalizePdfContentType(contentTypeHeader) });
  }

  const contentType = response.headers.get('content-type') ?? '';
  const rawBody = await response.text();

  if (!rawBody) {
    return null;
  }

  const trimmedRawBody = rawBody.trim();
  const looksLikeJson =
    contentType.includes('application/json') ||
    contentType.includes('application/problem+json') ||
    trimmedRawBody.startsWith('{') ||
    trimmedRawBody.startsWith('[');

  if (!looksLikeJson) {
    return rawBody;
  }

  try {
    return JSON.parse(rawBody) as Record<string, unknown>;
  } catch {
    return rawBody;
  }
};

const extendedCashDocumentsApi = cashDocumentsApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * QUERY: Get cash documents by reservation
     */
    getCashDocumentsByReservation: builder.query<CashDocumentDto[], number>({
      query: (reservationId) => `/cash-documents/reservation/${reservationId}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.CashDocument as const, id })),
              { type: TagTypes.CashDocument, id: TagTypes.List },
            ]
          : [{ type: TagTypes.CashDocument, id: TagTypes.List }],
    }),

    /**
     * QUERY: Get cash documents by invoice
     */
    getCashDocumentsByInvoice: builder.query<CashDocumentDto[], number>({
      query: (invoiceId) => `/cash-documents/invoice/${invoiceId}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagTypes.CashDocument as const, id })),
              { type: TagTypes.CashDocument, id: TagTypes.List },
            ]
          : [{ type: TagTypes.CashDocument, id: TagTypes.List }],
    }),

    /**
     * QUERY: Get cash balance for date range
     */
    getCashBalance: builder.query<
      CashBalanceResponse,
      { from: string; to: string; currency?: string }
    >({
      query: ({ from, to, currency }) => ({
        url: '/cash-documents/balance',
        params: cleanParams({ from, to, currency }),
      }),
      providesTags: [TagTypes.CashBalance],
    }),

    /**
     * QUERY: Get PDF bytes (for download)
     */
    getCashDocumentPdf: builder.query<Blob, number>({
      query: (id) => ({
        url: `/cash-documents/${id}/pdf`,
        method: 'GET',
        responseHandler: (response) => (response as Response).blob(),
      }),
      providesTags: (result, error, id) => [{ type: TagTypes.CashDocument, id }],
    }),

    /**
     * MUTATION: Regenerate PDF
     */
    regenerateCashDocumentPdf: builder.mutation<{ path: string }, number>({
      query: (id) => ({
        url: `/cash-documents/${id}/pdf/regenerate`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: TagTypes.CashDocument, id },
        { type: TagTypes.CashDocument, id: TagTypes.List },
      ],
    }),

    /**
     * QUERY: Get all cash documents (with filters + pagination)
     */
    getAllCashDocumentsFiltered: builder.query<
      PagedList<CashDocumentDto>,
      {
        type?: CashDocumentType;
        reservationId?: number;
        invoiceId?: number;
        personName?: string;
        documentNumber?: string;
        dateFrom?: string;
        dateTo?: string;
        pageNumber?: number;
        pageSize?: number;
      }
    >({
      query: (params) => ({
        url: '/cash-documents/filtered',
        params: cleanParams(params),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({ type: TagTypes.CashDocument as const, id })),
              { type: TagTypes.CashDocument, id: TagTypes.List },
            ]
          : [{ type: TagTypes.CashDocument, id: TagTypes.List }],
    }),

    /**
     * MUTATION: Create open cash-report cycle
     */
    createCashReportCycle: builder.mutation<
      CreateCashReportCycleResponse,
      CreateCashReportCycleRequest
    >({
      query: (body) => ({
        url: '/cash-reports/cycles',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: TagTypes.CashReportCycles, id: TagTypes.List }],
    }),

    /**
     * QUERY: List cash-report cycles
     */
    getCashReportCycles: builder.query<CashReportCycleListItem[], GetCashReportCyclesRequest | void>({
      query: (params) => ({
        url: '/cash-reports/cycles',
        params: cleanParams(params ?? {}),
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ cycleId }) => ({ type: TagTypes.CashReportCycles as const, id: cycleId })),
              { type: TagTypes.CashReportCycles, id: TagTypes.List },
            ]
          : [{ type: TagTypes.CashReportCycles, id: TagTypes.List }],
    }),

    /**
     * QUERY: Get cash-report cycle details
     */
    getCashReportCycleDetails: builder.query<CashReportCycleDetailsDto, number>({
      query: (cycleId) => ({
        url: `/cash-reports/cycles/${cycleId}`,
      }),
      providesTags: (result, error, cycleId) => [
        { type: TagTypes.CashReportCycleDetails, id: cycleId },
      ],
    }),

    /**
     * QUERY: Get cash-report cycle PDF bytes
     */
    getCashReportCyclePdf: builder.query<Blob, number>({
      async queryFn(cycleId, _queryApi, _extraOptions, baseQuery) {
        const queryResult = await baseQuery({
          url: `/cash-reports/cycles/${cycleId}/pdf`,
          method: 'GET',
          responseHandler: (response) => parseCashReportPdfResponse(response as Response),
        });

        if (queryResult.error) {
          return { error: queryResult.error };
        }

        if (queryResult.data === undefined) {
          return {
            error: {
              status: 500,
              data: {
                detail: 'Brak danych PDF raportu kasowego.',
                errorCode: CASH_REPORT_PDF_INVALID_RESPONSE_ERROR_CODE,
              },
            },
          };
        }

        const responseData = queryResult.data;
        const resolvedContentType = responseData instanceof Blob ? responseData.type : '';

        if (!(responseData instanceof Blob) || !isPdfContentType(resolvedContentType)) {
          return {
            error: {
              status: 422,
              data: {
                detail: 'Nieprawidłowy format odpowiedzi PDF raportu kasowego.',
                errorCode: CASH_REPORT_PDF_INVALID_RESPONSE_ERROR_CODE,
              },
            },
          };
        }

        return { data: responseData };
      },
      keepUnusedDataFor: 0,
      providesTags: (result, error, cycleId) => [
        { type: TagTypes.CashReportCycleDetails, id: cycleId },
      ],
    }),
  }),
});

/**
 * Export hooks for extended endpoints
 */
export const {
  useGetCashDocumentsByReservationQuery,
  useGetCashDocumentsByInvoiceQuery,
  useGetCashBalanceQuery,
  useGetCashDocumentPdfQuery,
  useLazyGetCashDocumentPdfQuery,
  useRegenerateCashDocumentPdfMutation,
  useGetAllCashDocumentsFilteredQuery,
  useCreateCashReportCycleMutation,
  useGetCashReportCyclesQuery,
  useGetCashReportCycleDetailsQuery,
  useLazyGetCashReportCyclePdfQuery,
} = extendedCashDocumentsApi;

export { extendedCashDocumentsApi };
